import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Container, Typography, Grid, Card, CardContent, Button, Tabs, Tab, Box } from '@mui/material';
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropTypes from "prop-types";
import { partnerService } from "services/partnerService";
import { AuthContext } from "context";
import { partnerDocumentService } from "services/partnerDocumentService";
import Footer from "examples/Footer";
import { CircularProgress } from "@mui/material";

// Partner Komponenten importieren
import PartnerInfo from "./PartnerInfo";
import PartnerDashboard from "./PartnerDashboard";
import CustomerAddForm from "./CustomerAddForm";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

// Document handling at the top level
const DownloadCell = React.memo(({ row }) => {
  const handleDownload = useCallback((event) => {
    event.preventDefault();
    if (row?.original?.fileUrl) {
      window.open(row.original.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }, [row]);

  if (!row?.original?.fileUrl) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={handleDownload}
    >
      Herunterladen
    </Button>
  );
});

DownloadCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      fileUrl: PropTypes.string.isRequired
    }).isRequired
  }).isRequired
};

const PartnerPage = () => {
  const { user } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    Vorname: "",
    Nachname: "",
    email: "",
    company: "",
    phone: "",
    username: "",
    password: "",
    billingemail: "",
    billingaddress: "",
    billingzip: "",
    billingcity: "",
    billingcountry: "Deutschland"
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });
  const [documents, setDocuments] = useState([]);
  const [activeDocTab, setActiveDocTab] = useState(0);
  const documentCategories = ["Whitepapers", "Präsentationen", "Preislisten", "Werbematerialen"];

  // Partner-Zustände
  const [isPartner, setIsPartner] = useState(false);

  const handleAddCustomer = () => {
    setShowAddCustomerDialog(true);
  };

  const handleCloseDialog = () => {
    setShowAddCustomerDialog(false);
    setCustomerFormData({
      Vorname: "",
      Nachname: "",
      email: "",
      company: "",
      phone: "",
      username: "",
      password: "",
      billingemail: "",
      billingaddress: "",
      billingzip: "",
      billingcity: "",
      billingcountry: "Deutschland"
    });
  };

  const handleSaveCustomer = async (formData) => {
    if (!user) {
      setSnackbar({
        open: true,
        message: "Nicht autorisiert. Bitte melden Sie sich erneut an.",
        color: "error"
      });
      return;
    }

    try {
      setLoading(true);
      const customerDataWithPartnerId = {
        ...formData,
        partnerId: user.id
      };
      const newCustomer = await partnerService.createCustomer(customerDataWithPartnerId);
      setCustomers(prev => [...prev, newCustomer]);
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: "Kunde erfolgreich angelegt",
        color: "success"
      });
    } catch (error) {
      console.error("Fehler beim Anlegen des Kunden:", error);
      setSnackbar({
        open: true,
        message: error.message || "Fehler beim Anlegen des Kunden",
        color: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const customerTableData = {
    columns: [
      { Header: "Name", accessor: "name", width: "25%" },
      { Header: "E-Mail", accessor: "email", width: "25%" },
      { Header: "Firma", accessor: "company", width: "25%" },
      { Header: "Status", accessor: "status", width: "25%" }
    ],
    rows: customers.map((customer) => ({
      name: `${customer.Vorname} ${customer.Nachname}`,
      email: customer.email,
      company: customer.company,
      status: customer.istactive ? "Aktiv" : "Inaktiv"
    }))
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDownloadDocument = useCallback((fileUrl) => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDocTabChange = useCallback((event, newValue) => {
    event.preventDefault();
    setActiveDocTab(newValue);
  }, []);

  // Document table column definitions
  const documentColumns = useMemo(() => [
    { Header: "Dokumenttitel", accessor: "title", align: "left" },
    { Header: "Beschreibung", accessor: "description", align: "left" },
    { Header: "Typ", accessor: "type", align: "center" },
    { Header: "Größe", accessor: "size", align: "center" },
    {
      Header: "Download",
      accessor: "actions",
      align: "center",
      Cell: DownloadCell
    }
  ], []);

  // Memoize filtered documents to prevent unnecessary re-renders
  const filteredDocuments = useMemo(() => {
    if (!Array.isArray(documents)) return [];
    return activeDocTab === 0
      ? documents
      : documents.filter(doc => doc.category === documentCategories[activeDocTab - 1]);
  }, [activeDocTab, documents, documentCategories]);

  // Memoize table data
  const documentTableData = useMemo(() => ({
    columns: documentColumns,
    rows: filteredDocuments,
  }), [documentColumns, filteredDocuments]);

  const [iaasCount, setIaasCount] = useState(0);
  const [saasCount, setSaasCount] = useState(0);
  const [saasDetails, setSaasDetails] = useState({ saas: 0, ecm: 0, epm: 0, auto: 0 });

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        setLoading(true);

        // Prüfen ob Benutzer Partner ist
        const partnerStatus = await partnerService.isPartner();
        setIsPartner(partnerStatus);

        // Nur wenn Partner, dann weitere Daten laden
        if (partnerStatus) {
          const [iaasStats, saasStats, customerList, documentList] = await Promise.all([
            partnerService.getIaasStats(user.id),
            partnerService.getSaasStats(user.id),
            partnerService.getCustomers(user.id),
            partnerDocumentService.getAllDocuments()
          ]);

          setIaasCount(iaasStats.count);
          setSaasCount(saasStats.count);
          setSaasDetails(saasStats.details);
          setCustomers(customerList);

          // Verarbeite die Dokumente bevor sie in den State gesetzt werden
          const processedDocs = Array.isArray(documentList)
            ? documentList.map(doc => ({
                ...doc,
                actions: doc.fileUrl
              }))
            : [];
          setDocuments(processedDocs);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
        setSnackbar({
          open: true,
          message: "Fehler beim Laden der Daten",
          color: "error"
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {loading ? (
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="calc(100vh - 200px)"
        >
          <CircularProgress color="info" />
        </SoftBox>
      ) : !isPartner ? (
        <PartnerInfo />
      ) : (
        <PartnerDashboard userId={user.id.toString()} />
      )}

      {/* Customer Add Dialog */}
      <Dialog
        open={showAddCustomerDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <CustomerAddForm
          onSubmit={handleSaveCustomer}
          onCancel={handleCloseDialog}
        />
      </Dialog>

      {/* Snackbar for notifications */}
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Hinweis"
        content={snackbar.message}
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        close={handleCloseSnackbar}
      />

      <Footer />
    </DashboardLayout>
  );
};

export default PartnerPage;
